import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../context/AuthContext';
import { useCreditHistory } from '../../hooks/useCreditHistory';

/**
 * UserActivityStats - Stats d'activité pour le Dashboard
 * Utilise les mêmes données que la page Settings > Usage
 */
const UserActivityStats = ({ loading: externalLoading = false }) => {
  const { userProfile } = useAuth();
  
  // Utiliser le même hook que la page Usage pour avoir les mêmes stats
  const { enrichedStats, loading: historyLoading } = useCreditHistory({ periodDays: '30' });
  
  const loading = externalLoading || historyLoading;

  // Données watermark depuis le profil
  const watermarkStats = useMemo(() => {
    const tracking = userProfile?.tracking || {};
    const saasUsage = userProfile?.saas_usage || {};
    const usage = userProfile?.usage || {};
    
    const totalNoWatermark = tracking.total_no_watermark ?? 0;
    const freeUsedToday = usage.freeNoWatermarkCountToday ?? saasUsage.quota_no_watermark_count_today ?? 0;
    const paidNoWatermark = Math.max(0, totalNoWatermark - freeUsedToday);
    
    return {
      total: totalNoWatermark,
      freeToday: freeUsedToday,
      paid: paidNoWatermark,
    };
  }, [userProfile]);

  // Stats combinées
  const stats = useMemo(() => ({
    totalSpent: enrichedStats?.totalSpent || 0,
    totalEarned: enrichedStats?.totalEarned || 0,
    totalTransactions: enrichedStats?.totalTransactions || 0,
    watermark: watermarkStats,
  }), [enrichedStats, watermarkStats]);

  // Formatage
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    return num.toLocaleString('fr-FR');
  };

  if (loading) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Ton activité</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">Statistiques de ton compte</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map(key => (
            <div key={key} className="h-[104px] rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Ton activité</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">Statistiques des 30 derniers jours</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Crédits dépensés */}
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-rose-600 dark:text-rose-400">
              <FontAwesomeIcon icon="arrow-trend-down" className="text-sm" />
            </div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Dépensé</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {formatNumber(stats.totalSpent)}
          </p>
          <p className="text-xs text-slate-400 mt-1">Sur la période</p>
        </div>

        {/* Crédits acquis */}
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <FontAwesomeIcon icon="arrow-trend-up" className="text-sm" />
            </div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Acquis</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {formatNumber(stats.totalEarned)}
          </p>
          <p className="text-xs text-slate-400 mt-1">Sur la période</p>
        </div>

        {/* Upscales */}
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-600 dark:text-sky-400">
              <FontAwesomeIcon icon="image" className="text-sm" />
            </div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Upscales</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {formatNumber(stats.totalTransactions)}
          </p>
          <p className="text-xs text-slate-400 mt-1">Jobs réalisés</p>
        </div>

        {/* Sans watermark */}
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400">
              <FontAwesomeIcon icon="download" className="text-sm" />
            </div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Sans watermark</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {formatNumber(stats.watermark.total)}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-emerald-600 dark:text-emerald-400">
              {stats.watermark.freeToday} gratuit
            </span>
            <span className="text-xs text-slate-300 dark:text-slate-600">•</span>
            <span className="text-xs text-amber-600 dark:text-amber-400">
              {stats.watermark.paid} à crédit
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserActivityStats;
